import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import ROICalculator from "@/components/landing/ROICalculator";

export default function Pricing() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    document.querySelectorAll('.fade-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const institutionalPlans = [
    {
      name: "Starter",
      price: "$5K-$8K",
      period: "/year",
      description: "Perfect for Div III, NAIA, and smaller programs",
      features: [
        "Up to 50 athletes",
        "Digital disclosure management",
        "Automated state rule compliance",
        "Basic deal approval workflow",
        "Prohibited category protection",
        "Audit trail & reporting",
        "Email support",
        "Mobile-friendly interface",
      ],
      cta: "Get Started",
      popular: false,
      marketSegments: ["div3", "naia", "highschool"],
    },
    {
      name: "Founding Partner Pilot",
      price: "$37,500",
      period: "/year (50% off Enterprise)",
      description: "Limited to first 10 institutions - help shape the future",
      features: [
        "Full Enterprise platform access",
        "6-month pilot with evaluation period",
        "Dedicated implementation team",
        "40 hours compliance officer training",
        "Direct product roadmap input",
        "Co-branded case study rights",
        "90-day money-back guarantee",
        "Lifetime preferred pricing lock",
        "Priority support",
      ],
      cta: "Request Pilot Access",
      popular: true,
      badge: "8 of 10 Spots Remaining",
      marketSegments: ["power5", "fcs", "div2"],
    },
    {
      name: "Growth",
      price: "$12K-$25K",
      period: "/year",
      description: "Best for Div II and FCS programs with growing NIL activity",
      features: [
        "50-200 athletes",
        "Everything in Starter, plus:",
        "Contract versioning & history",
        "Deal pipeline management",
        "ROI tracking dashboard",
        "Advanced compliance automation",
        "Real-time rule change alerts",
        "Priority email support",
        "Agent marketplace access",
        "Brand partnership opportunities",
      ],
      cta: "Get Started",
      popular: false,
      marketSegments: ["fcs", "div2"],
    },
    {
      name: "Enterprise Solution",
      price: "$75K-$150K",
      period: "/year",
      description: "Best for mid to large programs with active NIL activity",
      features: [
        "Unlimited athletes",
        "Everything in Foundation, plus:",
        "Advanced compliance automation",
        "Conflict filtering (institutional sponsors)",
        "Dedicated account manager",
        "Priority support",
        "Custom integrations (Learfield, BSG)",
        "Advanced deal analytics",
        "Batch approval workflows",
        "Comparative market analysis",
        "Enterprise audit defense (tamper-proof)",
        "NCAA audit-ready reporting",
        "Real-time alerts",
        "Multi-user access",
      ],
      cta: "Get Started",
      popular: false,
      marketSegments: ["power5", "fcs"],
    },
    {
      name: "Premier/Data Partner",
      price: "$150K-$250K",
      period: "/year",
      description: "Best for Power 5 programs and institutions wanting strategic advantage",
      features: [
        "Everything in Enterprise, plus:",
        "White-labeled athlete recruiting dashboard",
        "Custom compliance training modules",
        "Strategic NIL consulting",
        "Dedicated success team",
        "Custom data integrations",
        "API access",
        "Predictive compliance analytics",
        "Enterprise audit defense (tamper-proof)",
        "Athlete valuation modeling",
        "Competitive intelligence reports",
        "Board presentation templates",
        "Executive briefings",
      ],
      cta: "Contact Sales",
      popular: false,
      marketSegments: ["power5"],
    },
  ];

  const agencyPlans = [
    {
      name: "Base Model",
      price: "$5K-$15K",
      period: "/year + 1-3% transaction fee",
      description: "Flexible pricing for agencies and collectives",
      features: [
        "Access to athlete marketplace",
        "Multi-athlete campaign management",
        "Automated compliance disclosure",
        "Deal pipeline tracking",
        "Portfolio analytics",
        "Alternative: $50-$150 flat fee per disclosure",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "Unlimited",
      price: "$25K-$50K",
      period: "/year (no transaction fees)",
      description: "All-inclusive unlimited disclosures",
      features: [
        "All Base Model features",
        "Unlimited disclosures",
        "No per-transaction fees",
        "Advanced reporting",
        "Dedicated support",
      ],
      cta: "Contact Sales",
      popular: true,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="text-sm font-light tracking-wide text-foreground hover:opacity-70 transition-opacity"
          >
            AthleteNexus
          </button>
          <div className="flex items-center gap-8">
            <button
              onClick={() => navigate("/pricing")}
              className="text-sm font-light text-foreground transition-colors"
            >
              Pricing
            </button>
            <button
              onClick={() => window.open('/resources', '_blank')}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Resources
            </button>
            <button
              onClick={() => navigate("/auth")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-24 px-8">
        <div className="max-w-[800px] mx-auto text-center fade-on-scroll">
          <h1 className="text-[72px] md:text-[56px] sm:text-[40px] font-light leading-[1.1] tracking-tight text-foreground mb-6">
            Pricing
          </h1>
          <p className="text-2xl md:text-xl sm:text-lg font-light text-muted-foreground leading-relaxed max-w-[600px] mx-auto">
            Transparent pricing for institutions, agencies, and collectives.
          </p>
        </div>
      </section>

      {/* ROI Calculator */}
      <ROICalculator />

      {/* Divider */}
      <div className="max-w-[1200px] mx-auto px-8 mb-20">
        <div className="h-px bg-border/20" />
      </div>

      {/* Institutional Pricing */}
      <section className="pb-24 px-8">
        <div className="max-w-[1200px] mx-auto mb-20">
          <h2 className="text-[40px] md:text-[32px] sm:text-[24px] font-light tracking-tight text-foreground mb-4 fade-on-scroll">
            Institutional Pricing
          </h2>
          <p className="text-lg text-muted-foreground font-light fade-on-scroll">
            For colleges and universities managing NIL compliance
          </p>
        </div>

        <div className="max-w-[1200px] mx-auto grid md:grid-cols-3 gap-20 mb-32">
          {institutionalPlans.map((plan, index) => (
            <div 
              key={plan.name} 
              className="fade-on-scroll" 
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {plan.badge && (
                <Badge variant="outline" className="mb-4 text-xs uppercase tracking-wider border-primary/20 text-primary">
                  {plan.badge}
                </Badge>
              )}
              {plan.popular && !plan.badge && (
                <div className="text-xs uppercase tracking-wider text-foreground/40 mb-4 font-light">
                  Most Popular
                </div>
              )}
              
              <h3 className="text-xl font-light tracking-tight text-foreground mb-2">
                {plan.name}
              </h3>
              
              <p className="text-sm text-foreground/60 font-light mb-8">
                {plan.description}
              </p>
              
              <div className="mb-8">
                <span className="text-[56px] md:text-[44px] sm:text-[32px] font-light tracking-tight text-foreground">
                  {plan.price}
                </span>
                {plan.period && (
                  <span className="text-base text-foreground/40 font-light ml-2">
                    {plan.period}
                  </span>
                )}
              </div>
              
              <ul className="space-y-6 mb-12">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-foreground/40 flex-shrink-0 mt-0.5" />
                    <span className="text-base font-light text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                variant="minimal"
                size="lg"
                onClick={() => plan.cta === "Request Pilot Access" 
                  ? window.open("https://cal.com/jay-gauthier-the-mad-jotter-lhon2h/30min?overlayCalendar=true", "_blank")
                  : navigate("/auth")}
                className="w-full md:w-auto md:px-12"
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="max-w-[1200px] mx-auto mb-20">
          <div className="h-px bg-border/20" />
        </div>

        {/* Agency & Collective Pricing */}
        <div className="max-w-[1200px] mx-auto mb-20">
          <h2 className="text-[40px] md:text-[32px] sm:text-[24px] font-light tracking-tight text-foreground mb-4 fade-on-scroll">
            Agency & Collective Pricing
          </h2>
          <p className="text-lg text-muted-foreground font-light fade-on-scroll">
            Flexible options for agencies and collectives managing multiple athletes
          </p>
        </div>

        <div className="max-w-[800px] mx-auto grid md:grid-cols-2 gap-20">
          {agencyPlans.map((plan, index) => (
            <div 
              key={plan.name} 
              className="fade-on-scroll" 
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {plan.popular && (
                <div className="text-xs uppercase tracking-wider text-foreground/40 mb-4 font-light">
                  Most Popular
                </div>
              )}
              
              <h3 className="text-xl font-light tracking-tight text-foreground mb-2">
                {plan.name}
              </h3>
              
              <p className="text-sm text-foreground/60 font-light mb-8">
                {plan.description}
              </p>
              
              <div className="mb-8">
                <span className="text-[40px] md:text-[32px] sm:text-[24px] font-light tracking-tight text-foreground">
                  {plan.price}
                </span>
                {plan.period && (
                  <span className="text-sm text-foreground/40 font-light ml-2">
                    {plan.period}
                  </span>
                )}
              </div>
              
              <ul className="space-y-6 mb-12">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-foreground/40 flex-shrink-0 mt-0.5" />
                    <span className="text-base font-light text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                variant="minimal"
                size="lg"
                onClick={() => navigate("/auth")}
                className="w-full md:w-auto md:px-12"
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[1200px] mx-auto px-8 mb-20">
        <div className="h-px bg-border/20" />
      </div>

      {/* FAQ Section - No Cards */}
      <section className="pb-32 px-8">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-[48px] md:text-[40px] sm:text-[32px] font-light tracking-tight text-foreground mb-16 text-center fade-on-scroll">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-12 fade-on-scroll" style={{ animationDelay: '0.15s' }}>
            <div>
              <h3 className="text-xl font-light text-foreground mb-4">
                How does billing work?
              </h3>
              <p className="text-base text-foreground/60 font-light leading-relaxed">
                All plans are billed monthly or annually. You can upgrade, downgrade, or cancel at any time with changes taking effect at the start of your next billing cycle.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-light text-foreground mb-4">
                Can I change plans later?
              </h3>
              <p className="text-base text-foreground/60 font-light leading-relaxed">
                Yes! You can upgrade or downgrade at any time. Changes take effect at the start of your next billing cycle.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-light text-foreground mb-4">
                What payment methods do you accept?
              </h3>
              <p className="text-base text-foreground/60 font-light leading-relaxed">
                We accept all major credit cards, ACH transfers, and can arrange invoicing for Enterprise customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="border-t border-border/30 py-12 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <p className="text-sm font-light text-muted-foreground">
                © 2024 AthleteNexus. All rights reserved.
              </p>
              <Badge variant="security" className="text-[10px] uppercase tracking-wider">
                SOC 2 Type II Compliant
              </Badge>
            </div>
            <div className="flex items-center gap-8">
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Documentation
              </button>
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Support
              </button>
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Privacy
              </button>
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Terms
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
